// src/services/productService.js
import api from '../utils/api'; // Your API instance

const PRODUCT_BASE_URL = '/products';

const productService = {
  // Get all products with pagination, filtering, and search
  getProducts: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add search
      if (params.search) queryParams.append('search', params.search);
      
      // Add filters
      if (params.entityId) queryParams.append('entityId', params.entityId);
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
      if (params.popular !== undefined) queryParams.append('popular', params.popular);
      if (params.new !== undefined) queryParams.append('new', params.new);
      if (params.minAge) queryParams.append('minAge', params.minAge);
      if (params.maxAge) queryParams.append('maxAge', params.maxAge);
      
      // Add sorting
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await api.get(`${PRODUCT_BASE_URL}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get single product by ID
  getProductById: async (id) => {
    try {
      const response = await api.get(`${PRODUCT_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new product
  createProduct: async (productData) => {
    try {
      const response = await api.post(PRODUCT_BASE_URL, productData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update product
  updateProduct: async (id, productData) => {
    try {
      const response = await api.put(`${PRODUCT_BASE_URL}/${id}`, productData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete product
  deleteProduct: async (id) => {
    try {
      const response = await api.delete(`${PRODUCT_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Toggle product status
  toggleProductStatus: async (id) => {
    try {
      const response = await api.patch(`${PRODUCT_BASE_URL}/${id}/toggle-status`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Product Features
  addFeature: async (productId, featureData) => {
    try {
      const response = await api.post(`${PRODUCT_BASE_URL}/${productId}/features`, featureData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateFeature: async (productId, featureId, featureData) => {
    try {
      const response = await api.put(`${PRODUCT_BASE_URL}/${productId}/features/${featureId}`, featureData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteFeature: async (productId, featureId) => {
    try {
      const response = await api.delete(`${PRODUCT_BASE_URL}/${productId}/features/${featureId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Product Benefits
  addBenefit: async (productId, benefitData) => {
    try {
      const response = await api.post(`${PRODUCT_BASE_URL}/${productId}/benefits`, benefitData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateBenefit: async (productId, benefitId, benefitData) => {
    try {
      const response = await api.put(`${PRODUCT_BASE_URL}/${productId}/benefits/${benefitId}`, benefitData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteBenefit: async (productId, benefitId) => {
    try {
      const response = await api.delete(`${PRODUCT_BASE_URL}/${productId}/benefits/${benefitId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Product Sections
  addSection: async (productId, sectionData) => {
    try {
      const response = await api.post(`${PRODUCT_BASE_URL}/${productId}/sections`, sectionData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateSection: async (productId, sectionId, sectionData) => {
    try {
      const response = await api.put(`${PRODUCT_BASE_URL}/${productId}/sections/${sectionId}`, sectionData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteSection: async (productId, sectionId) => {
    try {
      const response = await api.delete(`${PRODUCT_BASE_URL}/${productId}/sections/${sectionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Product Q&A
  addQA: async (productId, qaData) => {
    try {
      const response = await api.post(`${PRODUCT_BASE_URL}/${productId}/qa`, qaData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateQA: async (productId, qaId, qaData) => {
    try {
      const response = await api.put(`${PRODUCT_BASE_URL}/${productId}/qa/${qaId}`, qaData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteQA: async (productId, qaId) => {
    try {
      const response = await api.delete(`${PRODUCT_BASE_URL}/${productId}/qa/${qaId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Pricing Tiers
  addPricingTier: async (productId, tierData) => {
    try {
      const response = await api.post(`${PRODUCT_BASE_URL}/${productId}/pricing`, tierData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updatePricingTier: async (productId, tierId, tierData) => {
    try {
      const response = await api.put(`${PRODUCT_BASE_URL}/${productId}/pricing/${tierId}`, tierData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deletePricingTier: async (productId, tierId) => {
    try {
      const response = await api.delete(`${PRODUCT_BASE_URL}/${productId}/pricing/${tierId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Eligibility Criteria
  addEligibility: async (productId, eligibilityData) => {
    try {
      const response = await api.post(`${PRODUCT_BASE_URL}/${productId}/eligibility`, eligibilityData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateEligibility: async (productId, eligibilityId, eligibilityData) => {
    try {
      const response = await api.put(`${PRODUCT_BASE_URL}/${productId}/eligibility/${eligibilityId}`, eligibilityData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteEligibility: async (productId, eligibilityId) => {
    try {
      const response = await api.delete(`${PRODUCT_BASE_URL}/${productId}/eligibility/${eligibilityId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Exclusions
  addExclusion: async (productId, exclusionData) => {
    try {
      const response = await api.post(`${PRODUCT_BASE_URL}/${productId}/exclusions`, exclusionData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateExclusion: async (productId, exclusionId, exclusionData) => {
    try {
      const response = await api.put(`${PRODUCT_BASE_URL}/${productId}/exclusions/${exclusionId}`, exclusionData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteExclusion: async (productId, exclusionId) => {
    try {
      const response = await api.delete(`${PRODUCT_BASE_URL}/${productId}/exclusions/${exclusionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Product Documents
  getProductDocuments: async (productId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      const response = await api.get(`${PRODUCT_BASE_URL}/${productId}/documents?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  requestPresignedUpload: async (productId, fileData) => {
    try {
      const response = await api.post(`${PRODUCT_BASE_URL}/${productId}/documents/presigned-upload`, fileData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  confirmDocumentUpload: async (productId, uploadData) => {
    try {
      const response = await api.post(`${PRODUCT_BASE_URL}/${productId}/documents/confirm`, uploadData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteDocument: async (productId, documentId) => {
    try {
      const response = await api.delete(`${PRODUCT_BASE_URL}/${productId}/documents/${documentId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Entities
  getEntities: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      
      const response = await api.get(`/entities?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Bulk delete
  bulkDeleteProducts: async (ids) => {
    try {
      const response = await api.delete(`${PRODUCT_BASE_URL}/bulk`, { data: { ids } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get product statistics
  getProductStats: async () => {
    try {
      const response = await api.get(`${PRODUCT_BASE_URL}/stats`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export default productService;